import { EVENTS, subscribe, publish } from 'ia-util-pubsub';

if (!customElements.get("ia-filter-component")) {
  class IaFilterComponent extends HTMLElement {
    connectedCallback() {
      this.addEventListener("change", (evt) => {
        const fromPriceChange = evt.target?.closest?.('[data-ia-price-range-input]');
        if (fromPriceChange) {
          const { ok, changed } = this.normalizePriceInputs();
    
          if (!ok) {
            // Still invalid (e.g., FROM > TO with one empty), do not submit.
            return;
          }
    
          if (changed) {
            // Values were auto-corrected this tick (e.g., TO set to FROM, or clamped).
            // Let the DOM reflect the new values, then submit once.
            queueMicrotask(() => this.onApplyFilters(evt));
            return;
          }
          // ok && !changed → already valid, fall through to immediate submit.
        }
    
        this.onApplyFilters(evt);
      });

      document.addEventListener("price-range-change", (event) => {
        const { minValue, maxValue } = event.detail;
        const minInput = this.querySelector('[data-ia-price-range-input="min"]');
        const maxInput = this.querySelector('[data-ia-price-range-input="max"]');

        if (minInput && minValue !== undefined)
          minInput.value = minValue > 0 ? minValue : "";
        if (maxInput && maxValue !== undefined) {
          const maxBound = parseFloat(maxInput.max);
          maxInput.value = maxValue < maxBound ? maxValue : "";
        }
      });

      this.subscribeToEvents();
    }
    
    getProductsArea() {
      return document.querySelector('[data-ia-products-area]');
    }

    subscribeToEvents() {
      subscribe(EVENTS.loadingStateChange, (event) => {
        this.handleLoadingStateChange(event.detail);
      });

      subscribe(EVENTS.filtersUpdate, (event) => {
        this.handleFiltersUpdate(event.detail);
      });

      subscribe(EVENTS.filtersError, (event) => {
        this.handleError(event.detail);
      });
    }

    handleLoadingStateChange({ isLoading }) {
      this.toggleLoading(isLoading);
    }

    handleFiltersUpdate({ html, bannerHtml, append }) {
      if (html) {
        this.renderProductsArea(html, append);
        
        if (!append) {
          this.renderFilterHTML(html);
          this.updateProductCount(html);
          if (bannerHtml) {
            this.updateBanner(bannerHtml);
          }
        }
      }
    }
    
    updateProductCount(html) {
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      const newCountElement = parsedDom.querySelector('[data-filter-count]');
      const countElements = document.querySelectorAll('[data-filter-count]');
      
      if (newCountElement && countElements.length > 0) {
        const count = newCountElement.textContent.trim();
        countElements.forEach(el => {
          el.textContent = count;
        });
      }
    }
    
    updateBanner(bannerHtml) {
      const currentBanner = document.querySelector('.section-banner');
      
      if (currentBanner && bannerHtml) {
        // Parse the banner section HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = bannerHtml;
        const newBannerContent = tempDiv.querySelector('.section-banner');
        
        if (newBannerContent) {
          currentBanner.innerHTML = newBannerContent.innerHTML;
        }
      }
    }
    
    handleError({ error }) {
      // console.error('Component Error:', error);
    }

    toggleLoading(isLoading = true) {
      const productsArea = this.getProductsArea();
      if (!productsArea) return;

      const loadingSpinners = document.querySelectorAll('[data-ia-filter-loading-spinner]');

      if (isLoading) {
        this.classList.add('loading');
        productsArea.classList.add('loading');
        loadingSpinners.forEach(spinner => spinner?.classList.remove('hidden'));
        if (this.applyButton) this.applyButton.disabled = true;
        
      } else {
        this.classList.remove('loading');
        productsArea.classList.remove('loading');
        loadingSpinners.forEach(spinner => spinner?.classList.add('hidden'));
        if (this.applyButton) this.applyButton.disabled = false;
      }
    }

    renderProductsArea(html, append = false) {
      const productsArea = this.getProductsArea();
      if (!productsArea) return false;
      
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      const newProductsArea = parsedDom.querySelector('.section-main-collection [data-ia-products-area]');
      
      if (!newProductsArea) return false;
      
      if (append) {
        // Append mode: append new products to existing grid
        const productGrid = productsArea.querySelector('[data-ia-product-grid]');
        const newProductGrid = newProductsArea.querySelector('[data-ia-product-grid]');
        
        if (!productGrid || !newProductGrid) return false;
        
        const newProducts = newProductGrid.querySelectorAll('.ia-collection-grid__product');
        if (newProducts.length === 0) return false;
        
        newProducts.forEach(item => {
          const productId = item.querySelector('[data-product-id]')?.getAttribute('data-product-id') || item.getAttribute('data-product-id');
          if (productId && !productGrid.querySelector(`[data-product-id="${productId}"]`)) {
            productGrid.appendChild(item.cloneNode(true));
          }
        });
      } else {
        // Replace mode: replace entire products area
        // Update collection data (it's outside products area, so needs separate update)
        const newCollectionData = parsedDom.querySelector('[data-ia-collection-data]');
        const currentCollectionData = document.querySelector('[data-ia-collection-data]');
        if (newCollectionData && currentCollectionData) {
          currentCollectionData.textContent = newCollectionData.textContent;
        }
        
        // First, remove pagination component if it exists
        const currentPaginationComponent = document.querySelector('ia-pagination-component');
        if (currentPaginationComponent) {
          currentPaginationComponent.remove();
        }
        
        // Get the new pagination component from parsed HTML before replacing innerHTML
        const newPaginationComponent = parsedDom.querySelector('ia-pagination-component');
        
        // Replace products area HTML (excluding pagination component for now)
        const newProductsAreaClone = newProductsArea.cloneNode(true);
        const paginationInClone = newProductsAreaClone.querySelector('ia-pagination-component');
        if (paginationInClone) {
          paginationInClone.remove();
        }
        productsArea.innerHTML = newProductsAreaClone.innerHTML;
        
        // Insert the new pagination component to trigger connectedCallback
        if (newPaginationComponent) {
          const newComponentClone = newPaginationComponent.cloneNode(true);
          productsArea.appendChild(newComponentClone);
        }
      }
      
      return true;
    }

    renderFilterHTML(html) {
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      
      // Update filter groups
      const currentFilterGroups = this.querySelectorAll('.ia-filter-group__content');
      const newFilterGroups = parsedDom.querySelectorAll('.ia-filter-group__content');
      
      currentFilterGroups.forEach((currentGroup, index) => {
        if (newFilterGroups[index]) {
          currentGroup.innerHTML = newFilterGroups[index].innerHTML;
        }
      });
      
      // Update mobile footer
      this.renderMobileFooter(parsedDom);
    }
    
    renderMobileFooter(parsedDom) {
      const currentFooter = document.querySelector('.ia-extra-option__footer');
      const newFooter = parsedDom.querySelector('.ia-extra-option__footer');
      
      if (currentFooter && newFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }
    }

    onApplyFilters(event) {
      event.preventDefault();

      const localForms = this.querySelectorAll(
        "form[data-ia-filter-form], form[data-ia-sort-form]",
      );

      const hasFilterForm = Array.from(localForms).some(form => form.hasAttribute('data-ia-filter-form'));
      const hasSortForm = Array.from(localForms).some(form => form.hasAttribute('data-ia-sort-form'));

      const allForms = [...localForms];
      
      if (!hasFilterForm) {
        const filterForm = document.querySelector('form[data-ia-filter-form]');
        if (filterForm) {
          allForms.push(filterForm);
        }
      }
      
      if (!hasSortForm) {
        const sortForm = document.querySelector('form[data-ia-sort-form]');
        if (sortForm) {
          allForms.push(sortForm);
        }
      }

      window.filterCore.applyFilters(allForms);
    }

    normalizePriceInputs() {
      const from = this.querySelector('[data-ia-price-range-input="min"]');
      const to   = this.querySelector('[data-ia-price-range-input="max"]');
    
      const num = (v) => (v === '' ? NaN : Number(v));
    
      const clamp = (el) => {
        if (!el) return { changed:false };
        // ensure native constraints are honored even if theme HTML used type="text"
        if (el.type !== 'number') el.type = 'number';
        el.inputMode = 'numeric';
    
        const min = el.min !== '' ? Number(el.min) : -Infinity;
        const max = el.max !== '' ? Number(el.max) :  Infinity;
        const before = el.value;
        let v = num(el.value);
    
        if (!Number.isNaN(v)) {
          if (v < min) v = min;
          if (v > max) v = max;
          el.value = String(v);
        } else if (el.value !== '') {
          // invalid (e.g., letters) → clear
          el.value = '';
        }
        return { changed: el.value !== before };
      };
    
      const c1 = clamp(from);
      const c2 = clamp(to);
    
      // keep FROM ≤ TO (only when both are numbers)
      const a = num(from?.value ?? '');
      const b = num(to?.value ?? '');
      let c3 = false;
      if (!Number.isNaN(a) && !Number.isNaN(b) && a >= b && to) {
        to.value = String(a);
        c3 = true;

        to.dispatchEvent(new Event('change', { bubbles: true }));
      }
    
      // Decide if it's safe to submit:
      // - OK if both are empty, or one is empty, or both are numbers AND a ≤ b.
      // - Not OK if any normalization changed the values this tick (we'll let the UI update first).
      const changedThisTick = (c1?.changed || c2?.changed || c3);
      const bothNums = !Number.isNaN(a) && !Number.isNaN(b);
      const ok =
        (!from || from.value === '') ||
        (!to || to.value === '') ||
        (bothNums && a <= b);
      
      return { ok, changed: changedThisTick };
    }
  }

  customElements.define("ia-filter-component", IaFilterComponent);
}

class IaLoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this._boundLoadMore = this.loadMore.bind(this);
    this._boundHandleFiltersUpdate = this.handleFiltersUpdate.bind(this);
    this._boundIntersectionCallback = this.handleIntersection.bind(this);
    this.isLoading = false;
    this.observer = null;
  }

  connectedCallback() {
    this.button?.addEventListener('click', this._boundLoadMore);
    
    this.unsubscribeFiltersUpdate = subscribe(EVENTS.filtersUpdate, this._boundHandleFiltersUpdate);
    this.unsubscribeLoadingState = subscribe(EVENTS.loadingStateChange, (event) => {
      this.handleLoadingStateChange(event.detail);
    });

    this.setupScrollMode();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this._boundLoadMore);
    this.unsubscribeFiltersUpdate?.();
    this.unsubscribeLoadingState?.();
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  setupScrollMode() {
    const mode = this.getAttribute('mode');
    if (mode === 'scroll') {
      this.observer = new IntersectionObserver(this._boundIntersectionCallback, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      });
      
      this.observer.observe(this);
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];
    
    if (entry.isIntersecting && !this.isLoading) {
      this.loadMore();
    }
  }

  handleLoadingStateChange({ isLoading }) {
    this.isLoading = isLoading;
    if (this.button) {
      this.button.disabled = isLoading;
    }
  }

  loadMore() {
    if (this.isLoading) {
      return;
    }

    publish(EVENTS.loadMoreRequested, {});
  }

  handleFiltersUpdate(event) {
    const { currentPage, totalPages, append } = event.detail;

    if (append === false) {
      this.classList.add('hidden');
      return;
    }

    if (totalPages === null) {
      this.classList.remove('hidden');
    } else if (currentPage >= totalPages) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }
}

if (!customElements.get('ia-load-more-button')) {
  customElements.define('ia-load-more-button', IaLoadMoreButton);
}

class IaPaginationComponent extends HTMLElement {
  constructor() {
    super();
    this.config = {
      productsPerBatch: 12,
      currentPage: 1,
      totalProducts: 0
    };
    this.pageItems = [];
    this.observer = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.handlePageClick.bind(this), true);
    this.unsubscribeFiltersUpdate = subscribe(EVENTS.filtersUpdate, this.handleFiltersUpdate.bind(this));
    this.init();
  }

  disconnectedCallback() {
    this.unsubscribeFiltersUpdate?.();
    this.observer?.disconnect();
  }

  // === INITIALIZATION ===

  init() {
    this.loadConfig();
    this.mapProducts();
    this.setupObserver();
    this.updateUI();
  }

  loadConfig() {
    const dataEl = document.querySelector('[data-ia-collection-data]');
    if (!dataEl) return;

    try {
      const data = JSON.parse(dataEl.textContent);
      this.config.totalProducts = data.totalProducts || 0;
      this.config.productsPerPage = data.productsPerPage || 48;
      this.config.productsPerBatch = data.productsPerScroll || 12;
      // Always start from page 1, ignore URL page parameter
      this.config.currentPage = 1;
    } catch (e) {
      // console.error('Failed to load collection data:', e);
    }
  }

  // === DOM HELPERS ===

  getProductGrid() {
    return document.querySelector('[data-ia-product-grid]');
  }

  // === PRODUCT MAPPING ===

  mapProducts() {
    const grid = this.getProductGrid();
    if (!grid) return;

    const products = Array.from(grid.querySelectorAll('.ia-collection-grid__product'));
    this.pageItems = products.map(el => ({
      element: el,
      rendered: el.style.display !== 'none'
    }));
  }

  // === INTERSECTION OBSERVER ===

  setupObserver() {
    this.observer?.disconnect();
  }

  showNextBatch() {
    const unrendered = this.pageItems.filter(item => !item.rendered);
    if (unrendered.length === 0) return;

    unrendered.slice(0, this.config.productsPerBatch).forEach(item => {
      item.element.style.display = '';
      item.rendered = true;
    });

    this.updateUI();
  }

  // === UI UPDATES ===

  updateUI() {
    // Calculate: items from previous pages + visible items on current page
    const itemsFromPreviousPages = (this.config.currentPage - 1) * this.config.productsPerPage;
    const visibleOnCurrentPage = this.pageItems.filter(item => item.rendered).length;
    const totalViewed = Math.min(itemsFromPreviousPages + visibleOnCurrentPage, this.config.totalProducts);
    
    const counterEl = this.querySelector('[data-ia-viewed-count]');
    if (counterEl) counterEl.textContent = totalViewed;
    
    const progressBar = this.querySelector('[data-ia-progress-bar]');
    if (progressBar && this.config.totalProducts > 0) {
      const percentage = (totalViewed / this.config.totalProducts) * 100;
      progressBar.style.width = `${Math.min(percentage, 100)}%`;
    }
    
    this.updateButton();
  }

  updateButton() {
    const wrapper = this.querySelector('.ia-collection__load-more-section');
    const button = this.querySelector('[data-ia-load-more-btn]');
    if (!wrapper) return;

    const allRendered = this.pageItems.every(item => item.rendered);
    const hasNextPage = !!this.querySelector('[data-ia-pagination-container] a[aria-label="Next page"]');

    // Always show wrapper for counter/progress
    wrapper.classList.remove('is-invisible');
    this.classList.remove('is-invisible');

    // Show button if there are hidden products OR there's a next page
    if (button) {
      const shouldShowButton = !allRendered || hasNextPage;
      this.style.display = shouldShowButton ? '' : 'none';
      button.disabled = false;
    }

    // Disconnect observer on last page
    if (allRendered && !hasNextPage) {
      this.observer?.disconnect();
    }
  }

  // === NAVIGATION ===

  handlePageClick(event) {
    const loadMoreBtn = event.target.closest('[data-ia-load-more-btn]');
    if (loadMoreBtn) {
      event.preventDefault();
      this.handleLoadMoreClick();
      return;
    }

    const navButton = event.target.closest('a.ia-pagination__nav');
    const pageButton = event.target.closest('a[data-page]');

    if (navButton) {
      event.preventDefault();
      event.stopPropagation();
      const isPrev = navButton.getAttribute('aria-label') === 'Previous page';
      isPrev ? this.navigateToPreviousPage() : this.navigateToNextPage();
    } else if (pageButton && !pageButton.classList.contains('active')) {
      event.preventDefault();
      event.stopPropagation();
      const targetPage = parseInt(pageButton.dataset.page);
      if (!isNaN(targetPage)) this.navigateToPage(targetPage);
    }
  }

  handleLoadMoreClick() {
    // First, check if there are hidden products on the current page
    const unrendered = this.pageItems.filter(item => !item.rendered);
    
    if (unrendered.length > 0) {
      // Show next batch of hidden products
      this.showNextBatch();
    } else {
      // All current products are visible, fetch next page
      this.navigateToNextPage();
    }
  }

  navigateToPage(page) {
    // Always start from page 1, so we can only navigate forward
    // Use filter-core's load more to reach target page
    if (page > this.config.currentPage && window.filterCore) {
      const pagesToLoad = page - this.config.currentPage;
      for (let i = 0; i < pagesToLoad; i++) {
        window.filterCore.handleLoadMore();
      }
    }
  }

  navigateToPreviousPage() {
    // Previous page not supported - always starts at page 1
    // Reset by triggering URL change (which resets to page 1)
    if (this.config.currentPage > 1 && window.filterCore) {
      window.filterCore.onURLChange();
    }
  }

  navigateToNextPage() {
    // Use filter-core's load more (uses internal counter)
    if (window.filterCore) {
      window.filterCore.handleLoadMore();
    }
  }

  // === AJAX UPDATES ===

  handleFiltersUpdate(event) {
    const { html, append } = event.detail;
    if (!html) return;

    this.updatePaginationHTML(html);
    // When appending, increment page. When replacing, always reset to page 1
    if (append) {
      this.config.currentPage = this.config.currentPage + 1;
    } else {
      this.config.currentPage = 1;
      this.pageItems = [];
    }

    setTimeout(() => {
      this.loadConfig();
      this.mapProducts();
      this.setupObserver();
      this.updateUI();
    }, 100);
  }

  updatePaginationHTML(html) {
    const parsedDom = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsedDom.querySelector('[data-ia-pagination-container]');
    const currentContainer = this.querySelector('[data-ia-pagination-container]');
    
    if (newContainer && currentContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
    }
  }
}

if (!customElements.get('ia-pagination-component')) {
  customElements.define('ia-pagination-component', IaPaginationComponent);
}

// Active Filters Component
class IaActiveFiltersComponent extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick);
  }

  handleClick(event) {
    const button = event.target.closest('[data-remove-filter], [data-clear-filters]');
    if (button) {
      event.preventDefault();
      const url = button.dataset.removeFilter || button.dataset.clearFilters;
      if (url) {
        history.pushState({}, '', url);
      }
    }
  }
}

if (!customElements.get('ia-active-filters-component')) {
  customElements.define('ia-active-filters-component', IaActiveFiltersComponent);
}

// Single SOLID Filter Reset Component
class IaFilterReset extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    // Use capture phase to intercept before link default behavior
    this.addEventListener('click', this._boundHandleClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick, true);
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const link = event.target.closest('a, button');
    if (link) {
      const url = link.getAttribute('href') || link.dataset.resetUrl;
      if (url) {
        // Close drawer if in mobile
        const drawer = this.closest('ia-drawer-component');
        if (drawer) {
          drawer.close();
        }
        
        // Small delay to allow drawer to close
        setTimeout(() => {
          history.pushState({}, '', url);
        }, drawer ? 100 : 0);
      }
    }
  }
}

if (!customElements.get('ia-filter-reset')) {
  customElements.define('ia-filter-reset', IaFilterReset);
}

// Apply Filters Button Component
class IaApplyFiltersButton extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._boundHandleClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick, true);
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const drawer = this.closest('ia-drawer-component');
    if (drawer) drawer.close();
    
    const forms = document.querySelectorAll('form[data-ia-filter-form], form[data-ia-sort-form]');
    window.filterCore?.applyFilters([...forms]);
  }
}

if (!customElements.get('ia-apply-filters-button')) {
  customElements.define('ia-apply-filters-button', IaApplyFiltersButton);
}
